"use client";
import type { GameData } from "@/app/games/(details)/types";
import { usePassportProvider } from "@/context";
import { Action, ControlType } from "@/context/AnalyticsProvider";
import { useGamePageAnalytics } from "@/context/AnalyticsProvider/gamePage";
import { useGameDetailsContext } from "@/context/GameDetailsContext";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import Image from "next/image";
import { useCallback } from "react";

interface FollowCTAProps {
  gameData: GameData;
  showOnboardingModal: () => void;
}

export const FollowCTA = ({
  gameData,
  showOnboardingModal,
}: FollowCTAProps) => {
  const { track } = useGamePageAnalytics();
  const { isWishlisted, addToWishlist, incompleteQuests, onQuestClick } =
    useGameDetailsContext();
  const nextQuest =
    incompleteQuests.length > 0 ? incompleteQuests[0] : undefined;

  const { newOnboardingFlow8BallNov25Gpt } = useFeatureFlags();

  const { isLoggedIn } = usePassportProvider();

  const handleClick = useCallback(async () => {
    track({
      action: Action.PRESSED,
      extras: {
        control: "follow-cta",
        controlLabel: nextQuest && isWishlisted ? "Next Quest" : "Follow Now",
        controlType: ControlType.BUTTON,
      },
    });

    if (!isLoggedIn) {
      if (newOnboardingFlow8BallNov25Gpt) {
        showOnboardingModal();
      } else {
        await addToWishlist();
      }
      return;
    }

    if (!isWishlisted) return await addToWishlist();

    if (nextQuest) return await onQuestClick(nextQuest);
    const url = gameData?.gameContent?.store_urls?.find((storeUrl) =>
      storeUrl?.includes("store.steampowered.com"),
    );
    if (url) window.open(url, "_blank");
    return;
  }, [
    isLoggedIn,
    nextQuest,
    isWishlisted,
    addToWishlist,
    track,
    onQuestClick,
    gameData,
    showOnboardingModal,
    newOnboardingFlow8BallNov25Gpt,
  ]);

  return (
    <button
      type="button"
      onClick={handleClick}
      className="z-50 md:z-auto md:!my-12 group fixed bottom-24 md:bottom-auto md:relative glass-border accent-border animated-border rounded-full w-full h-auto max-w-[90%] md:max-w-[28rem] px-8 @lg:px-12 py-2.5 flex items-center justify-between backdrop-blur-md accent-background hover:shadow-accent-glow transition-shadow duration-300 ease-in-out"
    >
      <div className="space-y-1 py-2.5 text-start">
        <p className="text-base @lg:text-lg font-medium !leading-none">
          {isWishlisted && nextQuest ? "Next Quest" : "Follow Now"}
        </p>
        <p className="text-xs @lg:text-sm !leading-none">
          {isWishlisted && nextQuest
            ? nextQuest.name
            : "Claim your special prize"}
        </p>
      </div>

      <Image
        src="/assets/steam-onboarding/crate-gold.webp"
        alt="Follow Prize"
        width={132}
        height={124}
        className="opacity-95 h-full w-auto absolute top-1/2 -translate-y-1/2 @lg:right-12 right-8 z-10"
      />

      <span aria-hidden className="duration-300 ease-in-out cta-glow" />
    </button>
  );
};
